import { useEffect, useMemo, useRef, useState } from "react";
import { X, Trash2, ChevronDown, ChevronUp, Bug, Copy, Check } from "lucide-react";

/**
 * GA4 Debug Overlay.
 *
 * Activation:
 *   - URL param `?debug=1`  (also persists for the session via sessionStorage)
 *   - or `sessionStorage.setItem('icb_debug', '1')`
 *   - Disable with `?debug=0` (or clear the storage key)
 *
 * Listens to `window` CustomEvent `icb:ga4` dispatched by `lib/analytics.trackEvent`.
 * Renders a floating panel with the latest events, parameters, and a copy/clear UI.
 *
 * Zero impact on production users: returns null unless explicitly enabled.
 */

const STORAGE_KEY = "icb_debug";
const MAX_EVENTS = 50;

// Pretty-print colours per event family (Tailwind palette tokens kept inline so
// the component stays self-contained and works even if Tailwind config changes).
const EVENT_COLOR = {
  page_view: "bg-slate-100 text-slate-700 border-slate-200",
  click_phone: "bg-blue-50 text-blue-700 border-blue-200",
  click_email: "bg-indigo-50 text-indigo-700 border-indigo-200",
  click_whatsapp: "bg-emerald-50 text-emerald-700 border-emerald-200",
  click_social: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
  click_directions: "bg-amber-50 text-amber-700 border-amber-200",
  click_reviews: "bg-yellow-50 text-yellow-800 border-yellow-200",
  generate_lead: "bg-green-100 text-green-800 border-green-300 font-semibold",
  form_error: "bg-red-50 text-red-700 border-red-200",
  consent_update: "bg-cyan-50 text-cyan-700 border-cyan-200",
};
const DEFAULT_COLOR = "bg-slate-50 text-slate-700 border-slate-200";

// Highlight the most relevant param per event family so the user spots it fast.
function summarise(name, params) {
  if (!params) return "";
  if (name === "click_social") return `${params.network || "?"} · ${params.location || params.source || "?"}`;
  if (name === "click_phone" || name === "click_email" || name === "click_whatsapp") return params.source || "";
  if (name === "click_directions" || name === "click_reviews") return params.source || "";
  if (name === "generate_lead") return `${params.service || "?"} · ${params.utm_source || "direct"}`;
  if (name === "form_error") return params.reason || params.field || "";
  if (name === "page_view") return params.page_title || "";
  return "";
}

function isEnabled() {
  if (typeof window === "undefined") return false;
  try {
    const url = new URL(window.location.href);
    const qp = url.searchParams.get("debug");
    if (qp === "1") {
      sessionStorage.setItem(STORAGE_KEY, "1");
      return true;
    }
    if (qp === "0") {
      sessionStorage.removeItem(STORAGE_KEY);
      return false;
    }
    return sessionStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export default function DebugOverlay() {
  const [enabled, setEnabled] = useState(() => isEnabled());
  const [events, setEvents] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const idRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    const handler = (e) => {
      const { name, params, ts } = e.detail || {};
      const id = ++idRef.current;
      setEvents((prev) => {
        const next = [{ id, name, params, ts }, ...prev];
        return next.slice(0, MAX_EVENTS);
      });
    };
    window.addEventListener("icb:ga4", handler);
    return () => window.removeEventListener("icb:ga4", handler);
  }, [enabled]);

  const counts = useMemo(() => {
    const c = {};
    for (const ev of events) c[ev.name] = (c[ev.name] || 0) + 1;
    return c;
  }, [events]);

  if (!enabled) return null;

  const disable = () => {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
    setEnabled(false);
  };

  const clear = () => {
    setEvents([]);
    setExpandedIdx(null);
  };

  const copyOne = async (idx, ev) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify({ name: ev.name, params: ev.params, ts: ev.ts }, null, 2));
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1200);
    } catch { /* noop */ }
  };

  return (
    <div
      data-testid="ga4-debug-overlay"
      className="fixed bottom-4 right-4 z-[9999] w-[360px] max-w-[92vw] rounded-xl bg-slate-900/95 backdrop-blur-xl text-slate-100 shadow-2xl border border-slate-700/70 font-mono text-[12px] leading-tight"
      style={{ maxHeight: "70vh" }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-700/60 cursor-pointer select-none"
           onClick={() => setCollapsed((v) => !v)}
           data-testid="ga4-debug-header">
        <Bug size={14} className="text-emerald-400 shrink-0" />
        <span className="font-semibold tracking-wide text-emerald-300">GA4 Debug</span>
        <span className="ml-1 text-slate-400">·</span>
        <span className="text-slate-300">{events.length} events</span>
        <div className="ml-auto flex items-center gap-1.5">
          <button
            onClick={(e) => { e.stopPropagation(); clear(); }}
            data-testid="ga4-debug-clear"
            title="Clear"
            className="p-1 rounded hover:bg-slate-700/70 text-slate-300 hover:text-white transition-colors"
          >
            <Trash2 size={13} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setCollapsed((v) => !v); }}
            data-testid="ga4-debug-toggle"
            title={collapsed ? "Expand" : "Collapse"}
            className="p-1 rounded hover:bg-slate-700/70 text-slate-300 hover:text-white transition-colors"
          >
            {collapsed ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); disable(); }}
            data-testid="ga4-debug-close"
            title="Disable overlay (this session)"
            className="p-1 rounded hover:bg-slate-700/70 text-slate-300 hover:text-white transition-colors"
          >
            <X size={13} />
          </button>
        </div>
      </div>

      {/* Body */}
      {!collapsed && (
        <>
          {/* Counters */}
          {events.length > 0 && (
            <div className="px-3 py-2 border-b border-slate-700/60 flex flex-wrap gap-1.5" data-testid="ga4-debug-counters">
              {Object.entries(counts).map(([k, v]) => (
                <span key={k} className={`px-1.5 py-0.5 rounded border text-[10px] ${EVENT_COLOR[k] || DEFAULT_COLOR}`}>
                  {k} · {v}
                </span>
              ))}
            </div>
          )}

          {/* Event list */}
          <div className="overflow-y-auto" style={{ maxHeight: "52vh" }} data-testid="ga4-debug-events">
            {events.length === 0 ? (
              <div className="px-3 py-6 text-center text-slate-400 text-[11px]">
                Aguardando eventos GA4…
                <br />
                <span className="text-slate-500">Clique nos telefones, email, ícones sociais ou submeta o formulário.</span>
              </div>
            ) : (
              events.map((ev, idx) => {
                const color = EVENT_COLOR[ev.name] || DEFAULT_COLOR;
                const time = new Date(ev.ts).toLocaleTimeString([], { hour12: false });
                const open = expandedIdx === idx;
                const summary = summarise(ev.name, ev.params);
                return (
                  <div key={ev.id} data-testid={`ga4-debug-row-${ev.name}`} className="border-b border-slate-800 last:border-0">
                    <button
                      onClick={() => setExpandedIdx(open ? null : idx)}
                      className="w-full text-left px-3 py-1.5 flex items-center gap-2 hover:bg-slate-800/60 transition-colors"
                    >
                      <span className="text-slate-500 text-[10px] shrink-0">{time}</span>
                      <span className={`px-1.5 py-0.5 rounded border text-[10px] shrink-0 ${color}`}>{ev.name}</span>
                      {summary && <span className="text-slate-300 text-[11px] truncate">{summary}</span>}
                      <span className="ml-auto text-slate-500 text-[10px]">
                        {open ? "▾" : "▸"}
                      </span>
                    </button>
                    {open && (
                      <div className="px-3 pb-2 pt-1 bg-slate-950/60">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] uppercase tracking-wider text-slate-500">params</span>
                          <button
                            onClick={(e) => { e.stopPropagation(); copyOne(idx, ev); }}
                            className="inline-flex items-center gap-1 text-[10px] text-slate-400 hover:text-white px-1.5 py-0.5 rounded hover:bg-slate-800"
                          >
                            {copiedIdx === idx ? <><Check size={11} /> copied</> : <><Copy size={11} /> copy</>}
                          </button>
                        </div>
                        <pre className="text-[10.5px] text-emerald-200/90 whitespace-pre-wrap break-all">
{JSON.stringify(ev.params, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer hint */}
          <div className="px-3 py-1.5 border-t border-slate-700/60 text-[10px] text-slate-500 flex items-center justify-between">
            <span>?debug=1 · session</span>
            <span>max {MAX_EVENTS}</span>
          </div>
        </>
      )}
    </div>
  );
}
